<template>
     <SubLayout
        :pageTitle="title"
        :pageBreadcrumbs="breadcrumbs"
        :hasBack="false"
        :loading="false"
    >
        <template #subHeaderActions>
            <div class="d-flex">
                <Button btn-class="me-3 btn-sm" v-if="payment?.status == 'UNPAID'" @click="openValidateModal()" type="submit" btn-text="button.validate" />
                <Button v-if="$can(abilities.CANCEL_PAYMENT)" type="cancel" v-show="payment?.status == 'PAID'" @click="cancelPayment()" btn-class="btn-sm me-3 bg-danger text-white" btn-text="button.cancel" ></Button>
                <Button v-if="$can(abilities.SEND_PAYMENT)" btn-class="me-3 btn-sm" v-show="payment?.status !== 'CANCEL'" @click="openSendModal" type="new" btn-text="crm.invoices.send" />
                <Button icon-class="fas fa-backspace" btn-class="btn-sm" type="cancel" btn-text="crm.back" @click="router.go(-1)"/>
            </div>
        </template>
        <template #layoutContent>
          <Loader :isLoading="isLoading">
            <div class="row d-flex flex-wrap px-5">
                <div class="card p-5 mb-4 me-md-5 flex-grow-1 col-12 col-md-3 col-lg-3">
                  <Button  v-if="$can(abilities.VIEW_INVOICE) || $can(abilities.VIEW_ALL_INVOICE)" btn-class="me-3 mb-5 btn-sm" @click="goToInvoice(payment?.invoice?.id)" icon-class="fas fa-file" type="new" height="45px" btn-text="invoices.invoice"></Button>
                  <div class="card shadow p-5" v-if="payment?.status == 'UNPAID'">
                      <div class="d-flex justify-content-between border-bottom py-5">
                          <div>{{ $t('invoices.paidAmount') }}</div>
                          <div>{{$formatLocalAmount(payment?.invoice?.total)}} {{ currency }}</div>
                      </div>
                      <div class="d-flex justify-content-between pt-5" style="color: var(--bs-primary);">
                          <div>{{ $t('invoices.remainAmount') }}</div>
                          <div>{{$formatLocalAmount(payment?.invoice?.remaining_payment)}} {{ currency }}</div>
                      </div>
                  </div>
                  <FileUploaded :attach-files="paymentFiles" :remove="false" />
                  <div class="card shadow-sm mt-5 p-5" v-show="payment.notes">
                      <div class="d-flex">
                        <h5 class="me-5 mb-3">{{ $t('crm.estimate.label.addNote') }} </h5>
                      </div>
                      <p>{{ payment?.notes }}</p>
                  </div>
                </div>
                <div class="card card-px-0 px-0 flex-grow-3 flex-md-grow-1 col-12 col-md-3 col-lg-3" style="flex: 1 1 60%;">
                    <Show :advance="payment?.status == 'UNPAID'" :payment="payment"/>
                    <div class="card-toolbar">
                        <AlertModal v-model:showingAlert="showingAlert" @cancel="cancelSend"/>
                    </div>
                </div>
           </div>
          </Loader>
        </template>
    </SubLayout>
    <SendModal id="sendModal" @submit="submitSend" :content="emailFormData" :is-loading="isLoadingSend" :title="$t('crm.payments.send')" type="PAYMENT" :file_number="$t('crm.payments.title') +' '+ payment.payment_number"/>
    <ValidateModal id="validateModal" @load="loadPayment" />
</template>

<script setup>
import { ref, watch, computed, shallowRef, onMounted } from "vue";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import { useRouter, useRoute } from 'vue-router';
import Loader from "@/components/Loader";
import Button from "@/components/Button";
import Show from "./components/Show.vue"
import SendModal from "@/Modules/CRM/components/Modals/SendModal.vue"
import {useSwal} from "@/composables/useSwal";
import {useStore} from "vuex";
import i18n from "@/plugins/i18n";
import {$formatLocalAmount} from "@/mixins/Helpers";
import AlertModal from '@/Modules/CRM/components/Modals/AlertModal.vue';
import { useLocales } from "@/composables/locales";
import ValidateModal from "@/Modules/CRM/components/Modals/Paiement/validateModal.vue"
import OpportunityFilesManagement from "@/Modules/CRM/views/Pipeline/components/OpportunityFilesManagement";
import FileUploaded from "@/components/FileUploaded";
import abilities from "@/composables/abilities";

const {locale} = useLocales()
const store = useStore()
const router = useRouter()
const route = useRoute()

const {showSwal} = useSwal()

const isLoading = ref(false)
const title = ref( "payment.payment")
const breadcrumbs = ref([
  {
    name: 'breadcrumbs.payment.list',
    route: 'payments',
    params: {}
  },
  {
    name: 'breadcrumbs.payment.detail',
    route: 'payments-view',
    params: route.params.id
  }
])

const emailFormData = shallowRef({
  paymentId: route.params.id,
  body: "",
  subject: i18n.global.t('crm.payments.title'),
  facture: "",
  to: [],
  from: ''
})

const isLoadingSend = ref(false)
const showingAlert = ref(false)

const paymentFiles = ref([])

const payment = computed(() => store.getters['crmPayments/currentPayment'])
const currency = computed(() => payment.value?.invoice?.currency?.symbol)
const company = computed(() => store.getters['company/getCompanyInfos'])

watch(() => company.value, () => {
  emailFormData.value.from = company.value?.society?.society_email
})

watch(() => payment.value, () => {
    if(payment.value?.invoice?.contact?.email){
        emailFormData.value.to = [payment.value?.invoice?.contact?.email]
    }
})

watch(() => payment.value.paymentFiles, (value) => {
  let data = []
  payment.value.paymentFiles?.map(fileData => {
    let file = fileData.file.split('.')
    let type = getFileType(file[file.length-1])
    data.push({ name: fileData.file, size: fileData.size || 0, url: fileData.file_url, type: type,id:fileData.id })
  })
  paymentFiles.value = data
})

function getFileType(type){
  let mimeType = ''

  switch (type) {
    case 'pdf':
      mimeType = 'application/pdf';
      break;
    case 'doc':
      mimeType = 'application/msword';
      break;
    case 'docx':
      mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      break;
    case 'jpeg':
    case 'jpg':
      mimeType = 'image/jpeg';
      break;
    case 'png':
      mimeType = 'image/png';
      break;
    default:
      mimeType = '';
  }

  return mimeType;
}

function resetData(){
  loadData()
}

function cancelSend(){
    store.dispatch('crmPayments/cancelPaymentMail',route.params.id)
        .then(() => {
            window.toastr['success'](i18n.global.t('notification.mail_cancelled'))
            showingAlert.value = false;
        })
        .catch(() => {
            showingAlert.value = false;
        })
}

function cancelPayment(){
    showSwal(
        i18n.global.t('crm.payments.swal.title_cancel'),
        i18n.global.t('crm.payments.swal.cancel')
    ).then( async (result) => {
        if (result.value) {
            try {
                store.dispatch('crmPayments/cancelPayment', {payment_id: route.params.id})
                .then(() => {
                    loadPayment()
                    window.toastr['success'](i18n.global.t('notification.payment_cancelled'))
                })
                .catch(() => {
                    
                })
            }catch (e) {

            }
        }
    });
}

function openValidateModal(){
    $('#validateModal').modal('toggle')
}

function openSendModal(){
    $('#sendModal').modal('toggle')
}

function goToInvoice(id){
    router.push({ name: 'invoices-view', params: {id: id} })
}

function submitSend() {
    isLoadingSend.value = true
    store.dispatch('crmPayments/sendPayment', emailFormData.value)
        .then(() => {
            $("#sendModal").modal('toggle');
            loadPayment()
            emailFormData.value.to = [payment.value?.invoice?.contact?.email]
            window.toastr['success'](i18n.global.t('notification.payment_send'))
            isLoadingSend.value = false
            alertDiv()
        })
        .catch(() => {
            isLoadingSend.value = false
        })
}

function loadPayment(){
    store.dispatch('crmPayments/getPaymentById', route.params.id)
}

function loadData () {
  Promise.all([
    loadPayment(),
    store.dispatch('payments/loadPaymentTypes'),
    store.dispatch('crmPayments/loadPaymentStatus'),
    store.dispatch('company/generalInformation')
  ])
}

onMounted(() => {
  loadData()
})

function alertDiv(){
    showingAlert.value = true;
    $('#sendModal').modal('toggle')
    setTimeout(() => {
        showingAlert.value = false;
      }, 5000);
}
</script>

<style scoped>
    .card-parent {
        position: relative;
    }

    .card-child {
        position: absolute;
        bottom: 0;
        width: 90%;
        font-weight: 500;
        margin-bottom: 20px;
    }
</style>